<template>
  <div class="blip-i18n-editor">
    <div class="blip-i18n-editor__tabs" role="tablist">
      <button
        v-for="loc in locales"
        :key="loc"
        type="button"
        role="tab"
        :data-locale="loc"
        :aria-selected="loc === activeLocale ? 'true' : 'false'"
        :class="['blip-i18n-editor__tab', { 'is-active': loc === activeLocale }]"
        @click="activeLocale = loc"
      >
        {{ localeLabels[loc] }}
        <span v-if="isBlank(value[loc])" class="blip-i18n-editor__badge">미작성</span>
      </button>
    </div>
    <!-- 로케일별 인스턴스를 v-show로 유지 → 탭 전환 시 undo/커서/포커스 격리. -->
    <blip-editor
      v-for="loc in locales"
      v-show="loc === activeLocale"
      :key="loc"
      :value="value[loc] || ''"
      :mode.sync="sharedMode"
      :tokens="tokens"
      :image="image"
      :font-size="fontSize"
      :placeholder="placeholder"
      :editable="editable"
      :upload-image="uploadImage"
      @input="onLocaleInput(loc, $event)"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import {
  BLIP_LOCALES,
  type BlipLocale,
  type BlipI18nHtml,
} from "@space-oddity-inc/editor-core";
import BlipEditor from "./BlipEditor.vue";

const LOCALE_LABELS: Record<BlipLocale, string> = {
  ko: "한국어",
  en: "English",
  ja: "日本語",
};

export default defineComponent({
  name: "BlipI18nEditor",
  components: { BlipEditor },
  props: {
    /** 로케일별 본문 HTML(v-model). 순수 `{@키}` 텍스트형(서버 계약형). 빈 로케일은 생략. */
    value: { type: Object as PropType<BlipI18nHtml>, default: () => ({}) },
    tokens: { type: Boolean, default: true },
    image: { type: Boolean, default: true },
    fontSize: { type: Boolean, default: true },
    placeholder: { type: String, default: "" },
    editable: { type: Boolean, default: true },
    uploadImage: {
      type: Function as PropType<(file: File) => Promise<string>>,
      default: undefined,
    },
  },
  data() {
    return {
      locales: BLIP_LOCALES,
      localeLabels: LOCALE_LABELS,
      activeLocale: BLIP_LOCALES[0] as BlipLocale,
      // 편집 방식을 전 로케일이 공유 → 한 탭에서 HTML 모드로 바꾸면 다른 탭도 따라옴.
      sharedMode: "wysiwyg" as "wysiwyg" | "html",
    };
  },
  methods: {
    isBlank(html?: string): boolean {
      if (!html) return true;
      return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, "").trim() === "";
    },
    onLocaleInput(loc: BlipLocale, html: string): void {
      // 활성 로케일만 갱신, 나머지 로케일 verbatim 보존(비파괴 머지).
      const next: BlipI18nHtml = { ...this.value, [loc]: html };
      this.$emit("input", next);
      this.$emit("update:value", next);
    },
  },
});
</script>

<style scoped>
.blip-i18n-editor__tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
}
.blip-i18n-editor__tab {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #374151;
  font-size: 13px;
  cursor: pointer;
}
.blip-i18n-editor__tab:hover {
  background: #f9fafb;
}
.blip-i18n-editor__tab.is-active {
  border-color: #c7d2fe;
  background: #e0e7ff;
  color: #3730a3;
  font-weight: 600;
}
.blip-i18n-editor__badge {
  padding: 0 5px;
  border-radius: 8px;
  background: #f3f4f6;
  color: #9ca3af;
  font-size: 10px;
  font-weight: 400;
}
.blip-i18n-editor__tab.is-active .blip-i18n-editor__badge {
  background: #c7d2fe;
  color: #4338ca;
}
</style>
